/**
 * Data Loader - Syncs data between Dexie (IndexedDB) and Firebase
 * Ensures user data is loaded from Firebase on login
 * 
 *  REFACTORED: Now uses Dexie as single source of truth (no localStorage for sessions)
 */

import { get1RMFromFirebase } from './firebaseSync';
import { syncAssessmentHistoryFromFirebase } from './assessmentStorage';
import { detectStaleData, clearSyncTimestamps } from './incrementalSync';
import { retryPendingFirebaseSyncs } from './sessionStorage';
import { needsMigration, migrateFromLocalStorage, db } from './db';
import { forceFullDownloadFromFirebase, syncAll } from './dexieFirebaseSync';
import { loadSavedModel, trainLoadModel, isMLModelReady } from './mlLoadRecommendation';
import { storage } from './storage';
import { LAUNCH_FLAGS } from './launchFlags';

/**
 * Load all user data from Firebase on login
 */
export async function loadUserDataFromFirebase(userId: string, useIncrementalSync: boolean = true): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    console.log('� Loading user data from Firebase...');
    
    // Load program selection FIRST (to check if program changed)
    //  Now uses Dexie (IndexedDB) - no localStorage needed
    const { getUserProgramSelection } = await import('./programManagement');
    const programSelection = await getUserProgramSelection(userId);
    
    if (programSelection) {
      console.log(` Loaded program selection: ${programSelection.programId} (Week ${programSelection.currentWeek}, Day ${programSelection.currentDay})`);
    }
    
    // Check for stale data before syncing
    const isStale = detectStaleData();
    if (isStale) {
      console.log(' Stale data detected - clearing sync timestamps for full resync');
      clearSyncTimestamps();
    }
    
    //  Use Dexie-based sync (bidirectional with Firebase)
    if (useIncrementalSync) {
      const syncResult = await syncAll(userId);
      const totalDownloaded = syncResult.downloaded.sessions + syncResult.downloaded.recovery;
      const totalUploaded = syncResult.uploaded.sessions + syncResult.uploaded.recovery;
      console.log(` Sync complete: ↓${totalDownloaded} downloaded, ↑${totalUploaded} uploaded`);
    } else {
      // Full download (fallback) - uses Dexie, not localStorage
      console.log('� Running full download from Firebase...');
      const downloadStats = await forceFullDownloadFromFirebase(userId);
      console.log(` Full download: ${downloadStats.sessions} sessions, ${downloadStats.recovery} recovery`);
    }
    
    // Backfill programSelection on existing sessions if needed
    if (programSelection) {
      console.log('� Calling backfillProgramSelection...');
      const { backfillProgramSelection } = await import('./backfillProgramSelection');
      await backfillProgramSelection();
      console.log('� backfillProgramSelection call completed');
    } else {
      console.log(' No programSelection - skipping backfill');
    }
    
    // Load 1RMs (still uses localStorage for state management)
    const oneRMs = await get1RMFromFirebase(userId);
    if (Object.keys(oneRMs).length > 0) {
      const existing = storage.get('tacticalFitnessState');
      const state = existing ? JSON.parse(existing) : {};
      state.oneRMs = oneRMs;
      storage.set('tacticalFitnessState', JSON.stringify(state));
      console.log(` Loaded ${Object.keys(oneRMs).length} 1RMs`);
    }
    
    // Load assessments (fitness tests)
    const assessmentCount = await syncAssessmentHistoryFromFirebase();
    if (assessmentCount > 0) {
      console.log(` Loaded ${assessmentCount} fitness test assessments`);
    }
    
    // Retry any pending Firebase syncs (sessions that failed to sync earlier)
    const retriedCount = await retryPendingFirebaseSyncs();
    if (retriedCount > 0) {
      console.log(` Retried and synced ${retriedCount} pending sessions to Firebase`);
    }
    
    // Migrate any stray localStorage sessions to Dexie
    // This catches sessions saved by old code that aren't in Dexie yet
    if (needsMigration()) {
      console.log('� Found localStorage data - migrating to Dexie...');
      const migrationStats = await migrateFromLocalStorage(userId);
      console.log(` Migration complete: ${migrationStats.sessions} sessions, ${migrationStats.recovery} recovery entries`);
      storage.set('dexie_migration_complete', 'true');
    }
    
    // Check if Dexie has incomplete data and needs full Firebase download
    // This recovers historical sessions that were never synced to this device
    const dexieSessionCount = await db.sessions.count();
    const fullDownloadKey = `full_firebase_download_${userId}`;
    const hasRunFullDownload = storage.get(fullDownloadKey);
    
    if (!hasRunFullDownload && dexieSessionCount < 30) {
      console.log(` Dexie has only ${dexieSessionCount} sessions - running full Firebase download...`);
      try {
        const downloadStats = await forceFullDownloadFromFirebase(userId);
        console.log(` Full download: ${downloadStats.sessions} sessions, ${downloadStats.recovery} recovery`);
        storage.set(fullDownloadKey, new Date().toISOString());
      } catch (error) {
        console.error(' Full download failed (will retry next load):', error);
      }
    }
    
    //  Initialize ML model for load recommendations.
    //  Gated OFF — see LAUNCH_FLAGS.tensorflowModels. This was spending a
    //  100-epoch background training run on app start to produce a model whose
    //  output the recommendation gate can never accept.
    const finalSessionCount = LAUNCH_FLAGS.tensorflowModels ? await db.sessions.count() : 0;
    if (finalSessionCount >= 20) {
      // Try to load saved model first
      const modelLoaded = await loadSavedModel();
      if (!modelLoaded) {
        // No saved model - train in background (don't block UI)
        console.log('� Training ML model in background...');
        trainLoadModel()
          .then(result => {
            if (result.success) {
              console.log(` ML model trained: ${result.message}`);
            } else {
              console.log(`ℹ ML training skipped: ${result.message}`);
            }
          })
          .catch(err => console.error('ML training error:', err));
      } else {
        console.log(' ML model restored from storage');
      }
    } else {
      console.log(`ℹ ML model requires 20+ sessions (current: ${finalSessionCount})`);
    }
    
    console.log(' User data loaded successfully');
    return { success: true };
    
  } catch (error) {
    console.error(' Error loading user data:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Check if user has any data (to determine if they're new)
 *  Now checks Dexie instead of localStorage
 */
export async function hasUserData(): Promise<boolean> {
  try {
    const sessionCount = await db.sessions.count();
    const recoveryCount = await db.recovery.count();
    const state = storage.get('tacticalFitnessState');

    return sessionCount > 0 || recoveryCount > 0 || !!state;
  } catch {
    // Fallback to localStorage check if Dexie fails
    const sessions = storage.get('sessionHistory');
    const recovery = storage.get('recoveryHistory');
    const state = storage.get('tacticalFitnessState');
    
    return !!(sessions || recovery || state);
  }
}
